import {kapitelErrorHandler} from "@/helper/error";
import {computed, Ref, ref, watch} from "vue";

type DataLoaderCallable = ((signal: AbortSignal, ...args : any[]) => Promise<any>) | DataLoader

export const InstantDataLoader = (dataLoaderCallable: DataLoaderCallable | DataLoaderCallable[]) : DataLoader => {
    const dataLoader = new DataLoader(dataLoaderCallable)
    dataLoader.load()
    return dataLoader
}

export class DataLoader {
    public pending : Ref<boolean>;
    public started : Ref<boolean>;
    public loading : Ref<boolean>;

    public done = computed(() => !this.pending.value)
    public result : any | any[] = ref(undefined)

    private loadingPromise : Promise<any> | undefined;
    private loadingAbortController: AbortController | undefined;

    private functions : Array<any> = [];
    private parameterPlural : boolean = false;

    public constructor(dataLoaderCallable: DataLoaderCallable | DataLoaderCallable[] | undefined = undefined) {
        this.pending = ref(true);
        this.started = ref(false);
        this.loading = ref(false);

        this._setupDebug()

        if (dataLoaderCallable) {
            this.parameterPlural = Array.isArray(dataLoaderCallable);

            this.functions = Array.isArray(dataLoaderCallable) ? dataLoaderCallable : [dataLoaderCallable];
        }
    }

    private async _load(...args : any[]) : Promise<any> {
        this.started.value = true

        if (!this.loadingPromise) {
            this.loadingAbortController = new AbortController()

            const executedFunctions = this.functions.map(f => f instanceof DataLoader ? f.load(...args) : f(this.loadingAbortController?.signal, ...args))

            this.loading.value = true
            this.loadingPromise = Promise
                .all(executedFunctions)
                .catch(kapitelErrorHandler)
                .finally(() => {
                    this.pending.value = false

                    this.loading.value = false
                    this.loadingPromise = undefined
                })
        }

        const result = await this.loadingPromise;

        this.result.value = this.parameterPlural ? result : result[0]

        return this.result.value
    }

    public load = this._load.bind(this)

    public cancel = () => {
        this.functions.map(f => f instanceof DataLoader ? f.cancel() : null)
        this.loadingAbortController?.abort()
    }

    public reload = async () : Promise<any> => {
        this.cancel()
        if (this.loadingPromise) {
            await this.loadingPromise;
        }
        this.reset()
        return this.load()
    }

    public reset = () => {
        this.loadingPromise = undefined
        this.pending.value = true
    }

    public add(dataLoaderCallable: DataLoaderCallable | DataLoaderCallable[]) {
        this.parameterPlural = true
        this.functions.push(...Array.isArray(dataLoaderCallable) ? dataLoaderCallable : [dataLoaderCallable]);
        this.pending.value = true
    }

    private _setupDebug() {
        if (!window.DEBUG.LOADER) {
            window.DEBUG.LOADER = ref(false);
        }
        watch(window.DEBUG.LOADER, (v:boolean) => this.pending.value = v);
    }
}
